// src/components/Events/Tree/TreeItemPalette.jsx

import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useDraggable } from "@dnd-kit/core";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

function resolveMediaUrl(url) {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return url;
}

/** ✅ 공통: 드래그 가능한 아이템(일반/콤팩트 모두 사용) */
function DraggableItem({ item, disabled, compact = false, size = 44 }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
        useDraggable({
            id: `item:${item.id}`,
            data: { item },
            disabled,
        });

    // ✅ 드래그 중 translate3d (스크린 px 기준)
    const style = transform
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
        : undefined;

    // ✅ 핵심: 아이템 자체는 항상 touchAction none (스크롤은 컨테이너가 담당)
    // - disabled면 터치 스크롤에 더 관대해도 되지만, 통일하는 게 제일 안정적
    // ✅ 모바일(compact)은 스와이프 스크롤이 우선이므로 pan-x 허용 
    // ✅ 드래그는 TreePage의 TouchSensor delay로 “롱프레스 후 드래그”로 동작 
    const itemTouchAction = compact ? "pan-x" : "none";

    if (compact) {
        return (
            <Paper
                ref={setNodeRef}
                {...listeners}
                {...attributes}
                sx={{
                    p: 0.8,
                    opacity: disabled ? 0.35 : isDragging ? 0.7 : 1,
                    cursor: disabled ? "not-allowed" : "grab",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    aspectRatio: "1 / 1",
                    userSelect: "none",
                    touchAction: itemTouchAction, // ✅ 여기 중요
                    width: "100%",
                    minWidth: 0,

                    // 모바일 사파리/크롬에서 롱프레스 이미지 선택/드래그 방지
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none",
                }}
                style={style}
            >
                <Box
                    component="img"
                    src={resolveMediaUrl(item.image)}
                    alt={item.name}
                    sx={{
                        width: size,
                        height: size,
                        objectFit: "contain",
                        pointerEvents: "none",
                        userSelect: "none",
                        WebkitUserDrag: "none",
                    }}
                    draggable={false}
                />
            </Paper>
        );
    }

    // ✅ 기본(PC): 아이콘 + 이름
    return (
        <Paper
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            sx={{
                p: 1,
                opacity: disabled ? 0.4 : isDragging ? 0.7 : 1,
                cursor: disabled ? "not-allowed" : "grab",
                userSelect: "none",
                touchAction: itemTouchAction, // ✅ 여기 중요
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
            }}
            style={style}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <Box
                    component="img"
                    src={resolveMediaUrl(item.image)}
                    alt={item.name}
                    sx={{
                        width: 44,
                        height: 44,
                        objectFit: "contain",
                        pointerEvents: "none",
                        userSelect: "none",
                        WebkitUserDrag: "none",
                    }}
                    draggable={false}
                />
                <Typography variant="body2">{item.name}</Typography>
            </Stack>
        </Paper>
    );
}

export default function TreeItemPalette({
    items = [],
    disabled = false,
    compact = false,
    title = "장식 아이템",
    helper = "아이템을 끌어서 트리 위에 놓아주세요.",
}) {
    const iconSize = compact ? 42 : 46;

    return (
        <Paper sx={{ p: compact ? 1.5 : 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 0.8 }}>
                {title}
            </Typography>

            {!compact && (
                <Typography variant="caption" color="text.secondary">
                    {helper}
                </Typography>
            )}

            {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    등록된 아이템이 없습니다.
                </Typography>
            ) : compact ? (
                // ✅ 모바일: 가로 스크롤 팔레트 (스크롤은 컨테이너만 담당)
                <Box sx={{ position: "relative", mt: 1 }}>
                    {/* ▶ 오른쪽 힌트 */}
                    <Box
                        sx={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 36,
                            zIndex: 2,
                            pointerEvents: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "linear-gradient(to left, rgba(255,255,255,0.9), transparent)",
                        }}
                    >
                        <ChevronRightIcon sx={{ opacity: 0.6 }} />
                    </Box>

                    {/* ◀ 왼쪽 힌트 */}
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 36,
                            zIndex: 2,
                            pointerEvents: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "linear-gradient(to right, rgba(255,255,255,0.9), transparent)",
                        }}
                    >
                        <ChevronLeftIcon sx={{ opacity: 0.3 }} />
                    </Box>

                    {/* ✅ 스크롤 컨테이너만 pan-x */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            overflowX: "auto",
                            overflowY: "hidden",
                            flexWrap: "nowrap",
                            WebkitOverflowScrolling: "touch",
                            touchAction: "pan-x", // ✅ 스크롤은 여기서만!
                            overscrollBehaviorX: "contain",
                            pb: 1,
                            px: 4,
                        }}
                    >
                        {items.map((it) => (
                            <Box key={it.id} sx={{ flex: "0 0 auto", width: 80 }}>
                                <DraggableItem
                                    item={it}
                                    disabled={disabled}
                                    compact
                                    size={iconSize}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            ) : (
                // ✅ PC: 세로 리스트
                <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    {items.map((it) => (
                        <DraggableItem key={it.id} item={it} disabled={disabled} />
                    ))}
                </Stack>
            )}
        </Paper>
    );
}
